import React, { createContext, useState, useEffect } from 'react';
import { fetchSettings as apiFetchSettings, saveSettings as apiSaveSettings } from '../api/settingsApi';

export const AssistantContext = createContext();

// ---------------------------
// Default (v2) settings shape
// ---------------------------
const DOC_ASSISTANT_SOLUTIONS = [
  "AEM",
  "AEP",
  "AJO",
  "Analytics",
  "Audience-Manager",
  "Campaign",
  "Commerce",
  "CJA",
  "KB-Articles",
  "Marketo",
  "Pass",
  "Target",
  "Workfront",
];

// Default LLMs list - frontend defines which LLMs to show and in what order
export const DEFAULT_LLMS = ["gpt-5.6-sol","claude-opus-5-5","claude-fable-5"];

const defaultSettings = {
  // Selected high-level mode (`chat`, `agent`, `doc assistant`)
  mode: "chat",

  // Chat-specific options
  chat: {
    llms: [...DEFAULT_LLMS],
    selectedLLM: "gpt-5.6-sol",
  },

  // Agent-specific options
  agent: {
    tools: ["docs", "workfront", "slack", "memory"],
    selectedTools: ["docs", "workfront", "slack", "memory"],
    showToolsSelection: false,
  },

  // Document assistants and their configuration
  assistant: {
    solutions: [...DOC_ASSISTANT_SOLUTIONS],
    selectedSolutions: [],
  },

  // Theme & UI
  theme: "dark",

  // Response configuration
  responseConfig: {
    tone: "professional", // professional, friendly, technical
    responseLength: "balanced", // concise, balanced, detailed
  },

  // User profile
  userProfile: {
    preferredName: "",
    jobTitle: "",
    organization: "",
    team: "",
    preferredLanguage: "",
  },

  // Custom prompts (saved by the user)
  customPrompts: [],

  // Data sources (payload merged in from backend or legacy settings)
  dataSources: {},
};

// Utility: shallow-deep merge that merges `chat`, `agent`, `assistant` sub-objects
const mergeSettings = (incoming) => {
  if (!incoming) return { ...defaultSettings };

  return {
    ...defaultSettings,
    ...incoming,
    chat: {
      ...defaultSettings.chat,
      ...(incoming.chat || {}),
      // Always use frontend-defined LLMs in frontend order, ignore backend LLMs
      llms: [...DEFAULT_LLMS],
    },
    agent: { ...defaultSettings.agent, ...(incoming.agent || {}) },
    assistant: {
      ...defaultSettings.assistant,
      ...(incoming.assistant || {}),
      // Always use frontend-defined solutions, ignore backend solutions list
      solutions: [...DOC_ASSISTANT_SOLUTIONS],
    },
  };
};

export const AssistantProvider = ({ children }) => {
  const [settings, setSettings] = useState(defaultSettings);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch settings from the backend on component mount
  useEffect(() => {
    fetchSettings();
  }, []);

  // Function to fetch settings from the backend
  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch settings from backend and merge with defaults
      const payload = await apiFetchSettings();
      const merged = mergeSettings(payload);

      setSettings(merged);

      // Update local theme if present
      if (merged.theme) {
        document.querySelector("html").setAttribute("data-theme", merged.theme);
        localStorage.setItem("theme", merged.theme);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      setError('Failed to load settings. Using defaults.');
      
      // If there's a theme in localStorage, apply it even if backend fetch failed
      const savedTheme = localStorage.getItem("theme");
      if (savedTheme) {
        document.querySelector("html").setAttribute("data-theme", savedTheme);
        setSettings(prev => ({...prev, theme: savedTheme}));
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to save settings to the backend
  const saveSettingsToBackend = async (newSettings) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the API service to save settings
      await apiSaveSettings(newSettings);
      
      // Update local state after successful save
      setSettings(newSettings);
      
      // Update theme in localStorage if it changed
      if (newSettings.theme !== settings.theme) {
        localStorage.setItem("theme", newSettings.theme);
      }
      
      return true; // Indicate success
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError('Failed to save settings to server.');
      return false; // Indicate failure
    } finally {
      setLoading(false);
    }
  };

  // Update helper merges the provided partial object with current settings
  // Update current settings. Patch should be a full logical state slice.
  // We intentionally build the merged state from defaults + patch ONLY to avoid
  // keeping outdated root-level keys such as `models` or `selectedModel`.
  const updateSettings = async (patch) => {
    const merged = mergeSettings(patch);
    const success = await saveSettingsToBackend(merged);
    return success;
  };

  return (
    <AssistantContext.Provider value={{ 
      settings, 
      updateSettings, 
      loading, 
      error, 
      refreshSettings: fetchSettings 
    }}>
      {children}
    </AssistantContext.Provider>
  );
};